import { useMemo } from "react";
import { buildHeaders } from "./apiConfig";
import { UPLOAD_API_URL } from "../constants";

// Internal: POST JSON to the Lambda endpoint
// ─────────────────────────────────────────────────────────────────────────────
const postJSON = async (payload) => {
  if (!UPLOAD_API_URL) {
    throw new Error(
      "REACT_APP_S3_API_URL is not configured. Add it to your .env file and restart the dev server."
    );
  }

  const url = `${UPLOAD_API_URL.replace(/\/$/, "")}/upload_files`;

  let res, json;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
    });
    json = await res.json().catch(() => ({}));
  } catch (networkErr) {
    throw new Error("NETWORK_ERROR");
  }

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      throw new Error("SESSION_EXPIRED");
    }
    if (res.status === 409) {
      throw new Error("DUPLICATE_JOB");
    }
    // Generic Lambda error message
    const detail = json.error || json.message || `HTTP ${res.status}`;
    throw new Error(detail);
  }

  return json;
};

export const usePollyMapping = (languages, voices) =>
  useMemo(
    () =>
      languages
        .filter((lang) => voices[lang])
        .map((lang) => ({
          language: lang,
          voice_id: voices[lang].name?.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
          engine: voices[lang].engine,
          gender: voices[lang].gender,
        })),
    [languages, voices]
  );
export const createUpload = async ({
  videoFile,
  srtFile,
  serviceType,
  pollyMapping,
  userEmail = "",
}) => {
  // eslint-disable-next-line no-console
  console.log(
    `[uploadFilesService] create_upload — ` +
      `video: "${videoFile.name}" (${(videoFile.size / (1024 * 1024)).toFixed(1)} MB), ` +
      `srt: "${srtFile.name}" (${(srtFile.size / 1024).toFixed(1)} KB)`
  );

  const result = await postJSON({
    action: "create_upload",
    videoFileName: videoFile.name.trim().toLowerCase(),
    videoSize: videoFile.size,
    videoContentType: videoFile.type || "video/mp4",
    srtFileName: srtFile.name.trim().toLowerCase(),
    srtSize: srtFile.size,
    languages: pollyMapping.map((m) => m.language),
    serviceType,
    pollyMapping,
    userEmail,
  });

  // Guards against Lambda returning snake_case (job_id, part_urls, etc.)
  // which would leave all camelCase destructuring as undefined.
  const jobId = result.jobId || result.job_id;
  const uploadId = result.uploadId || result.upload_id;
  const videoKey = result.videoKey || result.video_key;
  const srtKey = result.srtKey || result.srt_key;
  const srtUrl = result.srtUrl || result.srt_url;
  const partSize = result.partSize || result.part_size;

  // partUrls: normalise array of objects → array of plain URL strings
  // Handles: string[], { url }[], { presigned_url }[], { URL }[]
  const rawPartUrls = result.partUrls || result.part_urls || [];
  const partUrls = rawPartUrls.map((entry) => {
    if (typeof entry === "string") return entry;
    return entry.url || entry.presigned_url || entry.URL || entry.signedUrl || String(entry);
  });

  if (!jobId || !uploadId) {
    throw new Error("create_upload response is missing jobId or uploadId.");
  }
  if (!Array.isArray(partUrls) || partUrls.length === 0) {
    throw new Error("create_upload response returned no partUrls.");
  }
  if (!srtUrl) {
    throw new Error("create_upload response is missing srtUrl.");
  }

  // eslint-disable-next-line no-console
  console.log(
    `[uploadFilesService] Session created — jobId: ${jobId}, ` +
      `${partUrls.length} part(s), partSize: ${(partSize / (1024 * 1024)).toFixed(1)} MB`
  );

  return { jobId, uploadId, videoKey, srtKey, partUrls, srtUrl, partSize };
};

export const completeUpload = async ({ jobId, uploadId, videoKey, srtKey, completedParts }) => {
  // eslint-disable-next-line no-console
  console.log(
    `[uploadFilesService] complete_upload — jobId: ${jobId}, ${completedParts.length} part(s)`
  );

  const result = await postJSON({
    action: "complete_upload",
    jobId,
    uploadId,
    videoKey,
    srtKey,
    completedParts,
  });

  // eslint-disable-next-line no-console
  console.log(`[uploadFilesService] complete_upload response:`, result);
  return result;
};
